import React, { useEffect, useRef, useState } from "react";
import Translator, { __ } from "services/Translator";
import Slider from "react-slick";
import api from "services/api";
import { filterByCountry } from "utils/registrationStates";
import {
  getRequestCountry,
  resolveGeoCountry,
  COUNTRY_CHANGED_EVENT,
} from "services/requestCountry";
import DOMPurify from "dompurify";

import bannerOne600 from "assets/play365/images/banners/banner-one-phone.webp";
import bannerOne900 from "assets/play365/images/banners/banner-one-tab.webp";
import bannerOne1200 from "assets/play365/images/banners/banner-one-middle.webp";
import bannerOne1600 from "assets/play365/images/banners/banner-one-big.webp";

import bannerTwo600 from "assets/play365/images/banners/banner-two-phone.webp";
import bannerTwo900 from "assets/play365/images/banners/banner-two-tab.webp";
import bannerTwo1200 from "assets/play365/images/banners/banner-two-middle.webp";
import bannerTwo1600 from "assets/play365/images/banners/banner-two-big.webp";

import bannerOneVideo from "assets/play365/videos/banner-one-new.mp4";
import bannerTwoVideo from "assets/play365/videos/banner-two.mp4";
import bannerThreeVideo from "assets/play365/videos/banner-3rd-banner.mp4";

// vip page banners
import vipBanner600 from "assets/play365/images/banners/vip-banner.webp";
import vipBanner900 from "assets/play365/images/banners/vip-banner.webp";
import vipBanner1200 from "assets/play365/images/banners/vip-banner.webp";
import vipBanner1600 from "assets/play365/images/banners/vip-banner.webp";

//vip page banners
import wheelBanner600 from "assets/play365/images/banners/wheel-banner.webp";
import wheelBanner900 from "assets/play365/images/banners/wheel-banner.webp";
import wheelBanner1200 from "assets/play365/images/banners/wheel-banner.webp";
import wheelBanner1600 from "assets/play365/images/banners/wheel-banner.webp";

// The CMS API returns image fields as absolute URLs but the video field as a
// bare filename. Video and images share the same upload directory, so derive
// the full video URL by swapping the filename of any sibling image URL.
const resolveVideoUrl = (item) => {
  const videoFile = item?.videoBanner;
  if (!videoFile) return null;
  if (/^https?:\/\//i.test(videoFile)) return videoFile;

  const siblingImage = [
    item?.imageLargeDesktop,
    item?.imageMediumDesktop,
    item?.imageTablet,
    item?.imageMobile,
  ].find((url) => typeof url === "string" && /^https?:\/\//i.test(url));

  if (!siblingImage) return null;

  try {
    const url = new URL(siblingImage);
    url.pathname = url.pathname.replace(/[^/]+$/, videoFile);
    url.search = "";
    return url.toString();
  } catch (e) {
    return null;
  }
};

const getVideoType = (url) => {
  if (!url) return "video/mp4";
  const ext = url.split(/[?#]/)[0].split(".").pop().toLowerCase();
  if (ext === "webm") return "video/webm";
  if (ext === "ogg" || ext === "ogv") return "video/ogg";
  return "video/mp4";
};

export default function HomeFullBanner(props) {
  const [bannerImages, setBannerImages] = useState([]);
  // True until the first banner fetch resolves. While loading we render a neutral
  // placeholder instead of the static default carousel, so a reload (e.g. language
  // switch) never flashes the default banners before the country-specific ones.
  const [bannerLoading, setBannerLoading] = useState(true);
  // Guards against out-of-order responses: if a country switch fires a second
  // fetch while the first is still in flight, only the latest may set state, so a
  // slow stale (e.g. geo/default) response can never overwrite the newer one.
  const requestSeqRef = useRef(0);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true, //true on live server
    autoplaySpeed: 4000,
    fade: true,
    cssEase: "linear",
    arrows: true,
  };

  const getBannerImages = async () => {
    const seq = ++requestSeqRef.current;
    try {
      // Resolve the effective country (manual selector -> account -> geo) BEFORE the
      // request, so the X-Request-Country header the request carries and the
      // client-side country filter both use the same resolved country on a cold
      // first load. resolveGeoCountry populates the geo cache and respects a manual
      // override; getRequestCountry then returns the effective country.
      await resolveGeoCountry(api);
      const visitorCountry = getRequestCountry();
      const response = await api.get("/cms/home-banners/true", {
        sort: "sortOrder,ASC",
      });
      if (seq !== requestSeqRef.current) return; // superseded by a newer switch
      if (response.status === 200) {
        const data = response?.data?.data ?? [];
        setBannerImages(filterByCountry(Array.isArray(data) ? data : [], visitorCountry));
      } else {
        setBannerImages([]);
      }
    } catch (error) {
      console.error("Failed to fetch home banners:", error);
      if (seq === requestSeqRef.current) setBannerImages([]);
    } finally {
      if (seq === requestSeqRef.current) setBannerLoading(false);
    }
  };

  useEffect(() => {
    if (props.callFrom === "home") {
      getBannerImages();
    }
  }, [props.callFrom]);

  // Refetch in place when the visitor's country changes (manual pick or geo
  // resolving), keeping the current banners visible until the new ones arrive so
  // switching country never reloads the page or flashes.
  useEffect(() => {
    if (props.callFrom !== "home") return undefined;
    const onChange = () => getBannerImages();
    window.addEventListener(COUNTRY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(COUNTRY_CHANGED_EVENT, onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.callFrom]);

  const triggerModal = (setter, label) => {
    if (typeof setter === "function") {
      setter(true);
      setTimeout(() => setter(false), 500);
    } else if (process.env.NODE_ENV !== "production") {
      console.warn(`[HomeFullBanner] Missing handler for ${label}`);
    }
  };

  const handleSignupClick = () =>
    triggerModal(props.setModalSignUpPropsData, "setModalSignUpPropsData");

  // Open main cashier modal in DEPOSIT tab for logged-in users
  const handleDepositClick = () => {
    if (typeof props.setOpenWalletModalTab === "function") {
      props.setOpenWalletModalTab("deposit");
    } else if (process.env.NODE_ENV !== "production") {
      console.warn("[HomeFullBanner] Missing handler for setOpenWalletModalTab");
    }
  };

  const renderHomeBanner = (item, index) => {
    const safeTitle = item?.title || "";
    // Only show a description the record actually provides; do not fall back to a
    // long hardcoded blurb (per-country banners keep their copy short).
    const safeDescription = item?.description || "";
    const ctaForGuest = item?.buttonTextNonLoggedIn || __("Get Lucky Now!");
    const ctaForUser = item?.buttonTextLoggedIn || __("Deposit Now!");

    const videoUrl = resolveVideoUrl(item);
    const videoType = getVideoType(videoUrl);
    const videoPoster = item?.imageLargeDesktop || item?.imageMediumDesktop;

    return (
      <div className="banner-container" key={item?.id ?? index}>
        {videoUrl ? (
          <div className="banner-video-container">
            <video className="banner-video" autoPlay muted loop playsInline poster={videoPoster}>
              <source src={videoUrl} type={videoType} />
              <img src={videoPoster} alt="Casino Banner" className="banner-img" />
            </video>
          </div>
        ) : null}

        <picture className={`banner-image-container${videoUrl ? "" : " no-video"}`}>
          <source
            srcSet={item?.imageMobile || item?.imageMediumDesktop}
            media="(max-width: 600px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageTablet || item?.imageMediumDesktop}
            media="(max-width: 900px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageMediumDesktop || item?.imageLargeDesktop}
            media="(max-width: 1200px)"
            type="image/webp"
          />
          <source
            srcSet={item?.imageLargeDesktop || item?.imageMediumDesktop}
            media="(min-width: 1201px)"
            type="image/webp"
          />
          <img
            src={
              item?.imageMediumDesktop ||
              item?.imageLargeDesktop ||
              item?.imageTablet ||
              item?.imageMobile
            }
            alt="Casino Banner"
            className="banner-img"
          />
        </picture>

        <div className="bannerContent">
          <h1
            className="white"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(safeTitle.replace(/\n/g, "<br />")),
            }}
          />
          {item?.subHeading ? <h1 className="orange">{item.subHeading}</h1> : null}
          {safeDescription ? <p>{safeDescription}</p> : null}

          {!props.isAuthenticated && (
            <button type="button" onClick={handleSignupClick} className="commonBtn pOneBtn">
              {ctaForGuest}
            </button>
          )}

          {props.isAuthenticated && (
            <button type="button" onClick={handleDepositClick} className="commonBtn pOneBtn">
              {ctaForUser}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultHomeBanners = () => (
    <Slider {...settings}>
      <div className="banner-container slideOne">
        <div className="banner-video-container">
          <video
            className="banner-video"
            autoPlay
            muted
            loop
            playsInline
            poster={bannerOne1600}
          >
            <source src={bannerOneVideo} type="video/mp4" />
            <img
              src={bannerOne1600}
              alt="Casino Banner"
              className="banner-img"
            />
          </video>
        </div>

        <picture className="banner-image-container">
          <source srcSet={bannerOne600} media="(max-width: 600px)" type="image/webp" />
          <source srcSet={bannerOne900} media="(max-width: 900px)" type="image/webp" />
          <source srcSet={bannerOne1200} media="(max-width: 1200px)" type="image/webp" />
          <source srcSet={bannerOne1600} media="(min-width: 1201px)" type="image/webp" />
          <img src={bannerOne1200} alt="Casino Banner" className="banner-img" />
        </picture>

        <div className="bannerContent ">
          <h1 className="white">{__("CLOVER CHASERS")} <br/> {__("FORTUNE MAKERS")}</h1>
          <h1 className="orange">{__("GOLDEN CLOVER CASINO")}</h1>
          <p>
            {__(
              "Massive welcome package, grab your loot! Up to $7,000 bonus plus 777 free spins. Join the clover family and start winning."
            )}
          </p>
          {!props.isAuthenticated && (
            <button type="button" onClick={handleSignupClick} className="commonBtn pOneBtn">
              {__("Get Lucky Now!")}
            </button>
          )}
          {props.isAuthenticated && (
            <button type="button" onClick={handleDepositClick} className="commonBtn pOneBtn">
              {__("Deposit Now!")}
            </button>
          )}
        </div>
      </div>

      <div className="banner-container slideTwo">
        <div className="banner-video-container">
          <video
            className="banner-video"
            autoPlay
            muted
            loop
            playsInline
            poster={bannerTwo1600}
          >
            <source src={bannerTwoVideo} type="video/mp4" />
            <img
              src={bannerTwo1600}
              alt="Casino Banner"
              className="banner-img"
            />
          </video>
        </div>

        <picture className="banner-image-container">
          <source srcSet={bannerTwo600} media="(max-width: 600px)" type="image/webp" />
          <source srcSet={bannerTwo900} media="(max-width: 900px)" type="image/webp" />
          <source srcSet={bannerTwo1200} media="(max-width: 1200px)" type="image/webp" />
          <source srcSet={bannerTwo1600} media="(min-width: 1201px)" type="image/webp" />
          <img src={bannerTwo1200} alt="Casino Banner" className="banner-img" />
        </picture>

        <div className="bannerContent">
          <h1 className="orange">{__("CHASE THE POT O' GOLD")}</h1>
          <h1 className="white">{__("AT GOLDEN CLOVER CASINO")}</h1>
          <p>
            {__(
              "Ready to rise? Scale the Emerald Ranks, Grab Loot, Top-Shelf VIP Perks, Daily Freebies & More. Be the Legend. Secure Your Spot Among Our Elite Winners"
            )}
          </p>
          {!props.isAuthenticated && (
            <button type="button" onClick={handleSignupClick} className="commonBtn pOneBtn">
              {__("Get Lucky Now!")}
            </button>
          )}
          {props.isAuthenticated && (
            <button type="button" onClick={handleDepositClick} className="commonBtn pOneBtn">
              {__("Deposit Now!")}
            </button>
          )}
        </div>
      </div>

      <div className="banner-container slideThree">
        <div className="banner-video-container">
          <video
            className="banner-video"
            autoPlay
            muted
            loop
            playsInline
            poster={bannerTwo1600}
          >
            <source src={bannerThreeVideo} type="video/mp4" />
            <img
              src={bannerTwo1600}
              alt="Casino Banner"
              className="banner-img"
            />
          </video>
        </div>

        <picture className="banner-image-container">
          <source srcSet={bannerTwo600} media="(max-width: 600px)" type="image/webp" />
          <source srcSet={bannerTwo900} media="(max-width: 900px)" type="image/webp" />
          <source srcSet={bannerTwo1200} media="(max-width: 1200px)" type="image/webp" />
          <source srcSet={bannerTwo1600} media="(min-width: 1201px)" type="image/webp" />
          <img src={bannerTwo1200} alt="Casino Banner" className="banner-img" />
        </picture>

        <div className="bannerContent">
          <h1 className="orange">{__("PLAY YOUR FAVOURITE GAMES")}</h1>
          <h1 className="white">{__("AND IGNITE YOUR WIN STREAK")}</h1>
          <p>
            {__(
              "Thrilling Slots, Exciting Crash Games, Bonus Drops, Megaways, Live Casino. You name it, we got it. Over 7,000 games of all kinds available. Endless ways to win BIG!"
            )}
          </p>
          {!props.isAuthenticated && (
            <button type="button" onClick={handleSignupClick} className="commonBtn pOneBtn">
              {__("Get Lucky Now!")}
            </button>
          )}
          {props.isAuthenticated && (
            <button type="button" onClick={handleDepositClick} className="commonBtn pOneBtn">
              {__("Deposit Now!")}
            </button>
          )}
        </div>
      </div>
    </Slider>
  );

  return (
    <>
      <div className={`homeBannerFull ${props.className}`}>

        {props.callFrom === "home" && (
          <>
            {bannerImages.length > 1 ? (
              <Slider
                key={bannerImages
                  .map((b) => b.id ?? b.imageLargeDesktop ?? b.title)
                  .join("|")}
                {...settings}
              >
                {bannerImages.map(renderHomeBanner)}
              </Slider>
            ) : bannerImages.length === 1 ? (
              bannerImages.slice(0, 1).map(renderHomeBanner)
            ) : bannerLoading ? (
              <div className="home-banner-loading" aria-hidden="true" />
            ) : (
              renderDefaultHomeBanners()
            )}
          </>
        )}



        {props.callFrom === "vip" && (
          <div className="banner-container slideOne">
            {/* Image for all screens */}
            <picture className="banner-image-container">
              <source
                srcSet={vipBanner600}
                media="(max-width: 600px)"
                type="image/webp"
              />
              <source
                srcSet={vipBanner900}
                media="(max-width: 900px)"
                type="image/webp"
              />
              <source
                srcSet={vipBanner1200}
                media="(max-width: 1200px)"
                type="image/webp"
              />
              <source
                srcSet={vipBanner1600}
                media="(min-width: 1201px)"
                type="image/webp"
              />
              <img
                src={vipBanner1200}
                alt="Casino Banner"
                className="banner-img"
              />
            </picture>

            <div className="bannerContent ">
              <h1 className="orange">{__("Gamble on Anything")}</h1>
              <h1 className="white">{__("With Golden Clover")}</h1>
              <p>
                {__(
                  "Do you have what it takes? Highroller Online Casino, Slots, Games, In-Play Sports Betting, Online Poker & More. Under one roof. Access Using Over 33 Cryptocurrencies"
                )}
              </p>

              {!props.isAuthenticated && (
                <button
                  type="button"
                  onClick={handleSignupClick}
                  className="commonBtn pOneBtn"
                >
                  {__("Get Lucky Now!")}
                </button>
              )}

              {props.isAuthenticated && (
                <button
                  type="button"
                  onClick={handleDepositClick}
                  className="commonBtn pOneBtn"
                >
                  {__("Deposit Now!")}
                </button>
              )}
            </div>
          </div>
        )}

        {props.callFrom === "wheel" && (
          <div className="banner-container slideOne">
            {/* Image for all screens */}
            <picture className="banner-image-container">
              <source
                srcSet={wheelBanner600}
                media="(max-width: 600px)"
                type="image/webp"
              />
              <source
                srcSet={wheelBanner900}
                media="(max-width: 900px)"
                type="image/webp"
              />
              <source
                srcSet={wheelBanner1200}
                media="(max-width: 1200px)"
                type="image/webp"
              />
              <source
                srcSet={wheelBanner600}
                media="(min-width: 1201px)"
                type="image/webp"
              />
              <img
                src={wheelBanner1200}
                alt="Casino Banner"
                className="banner-img"
              />
            </picture>

            <div className="bannerContent ">
              <h1 className="orange">{__("FEEL THE CLOVER LUCK")}</h1>
              <h1 className="white">{__("WEEKLY WHEELS BONANZA")}</h1>
              <p>
                {__(
                  "Three wheels, endless prizes! Spin the Bronze, Silver, or Gold Wheel depending on your VIP status. Each spin guarantees a win, from free spins, bonuses, VIP level-up, cash, and more! Each week brings new thrilling spins..."
                )}
              </p>

              {!props.isAuthenticated && (
                <button
                  type="button"
                  onClick={handleSignupClick}
                  className="commonBtn pOneBtn"
                >
                  {__("Get Lucky Now!")}
                </button>
              )}

              {props.isAuthenticated && (
                <button
                  type="button"
                  onClick={handleDepositClick}
                  className="commonBtn pOneBtn"
                >
                  {__("Deposit Now!")}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
